import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useProducts } from './useProducts';

export interface Bundle {
  id: string;
  name: string;
  slug: string;
  description: string;
  discountPercentage: number;
  productIds: string[];
  isActive: boolean;
  displayOrder: number;
  products?: Array<{
    id: number;
    name: string;
    price25g: string;
    productImage: string;
  }>;
  originalPrice?: number;
  bundlePrice?: number;
}

export const useBundles = () => {
  const [bundles, setBundles] = useState<Bundle[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { products } = useProducts();

  const fetchBundles = async () => {
    try {
      setIsLoading(true);
      const { data, error: fetchError } = await supabase
        .from('bundles')
        .select('*')
        .eq('is_active', true)
        .order('display_order');

      if (fetchError) throw fetchError;

      // Transform and enrich with product data
      const enrichedBundles = (data || []).map(bundle => {
        const productIdsArray = Array.isArray(bundle.product_ids) 
          ? bundle.product_ids as string[]
          : [];
          
        const bundleProducts = products.filter(p => 
          productIdsArray.includes(String(p.id)) || productIdsArray.includes(p.slug)
        );

        const originalPrice = bundleProducts.reduce((sum, p) => 
          sum + parseFloat(p.price25g.replace('€', '').replace(',', '.')), 0
        );

        const bundlePrice = originalPrice * (1 - bundle.discount_percentage / 100);

        return {
          id: bundle.id,
          name: bundle.name,
          slug: bundle.slug,
          description: bundle.description || '',
          discountPercentage: bundle.discount_percentage,
          productIds: productIdsArray,
          isActive: bundle.is_active,
          displayOrder: bundle.display_order,
          products: bundleProducts.map(p => ({
            id: p.id,
            name: p.name,
            price25g: p.price25g,
            productImage: p.productImage,
          })),
          originalPrice,
          bundlePrice,
        };
      });

      setBundles(enrichedBundles);
      setError(null);
    } catch (err) {
      console.error('Error fetching bundles:', err);
      setError('Failed to load bundles');
      setBundles([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (products.length > 0) {
      fetchBundles();
    } else {
      setBundles([]);
      setIsLoading(false);
    }
  }, [products]);

  return { bundles, isLoading, error, refetch: fetchBundles };
};
